import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Cards from "./Card";
import Cornerstone from "@assets/integrations/cornerstone.png";
import Avimark from "@assets/integrations/avimark.png";
import Impromed from "@assets/integrations/impromed.png";
import Vetspire from "@assets/integrations/vetspire.png";
import EzyVet from "@assets/integrations/ezyvet.png";
import ShepherdLogo from "@assets/integrations/shepherd.png";
import DaySmartLogo from "@assets/integrations/daysmart.png";
import PulseLogo from "@assets/integrations/pulse.png";
import ArrowSquareUpIcon from "@icons/arrow-square-up.svg";
import { useAccountContext } from "@providers/AccountProvider";
import ConnectedIntegration from "./ConnectedIntegration";
import { FeatureFlags } from "@/common/utils/types";
import Divider from "@/common/components/Divider";
import { VITE_PUBLIC_ENV } from "@/common/utils/constants";
import BridgeDownload from "../bridgedownload";
import { bridgeStatusQueryKey, fetchBridgeStatus } from "@common/lib/api/bridge";

/**
 * On-premise PIMS, matched on `connected_third_party`. They are reached through HappyDoc Bridge
 * (or, for accounts not yet cut over, the legacy Bitwerx connector).
 */
export enum IntegrationThirdParty {
    AVImark = "AVImark",
    ImproMed = "ImproMed",
    Cornerstone = "Cornerstone",
    EzyVet = "EzyVet",
}

/**
 * Represents the type of integrations that we support
 */
export enum IntegrationProvider {
    VetSpire = "vetspire",
    Bitwerx = "bitwerx",
    EzyVet = "ezyvet",
    Shepherd = "shepherd",
    DaySmart = "daysmart",
    Pulse = "pulse",
    File = "file",
    HappyBridge = "happybridge",
}

export type Integration = {
    id: number;
    name: string;
    provider: IntegrationThirdParty | IntegrationProvider;
    imageUrl: string;
    disabled: boolean;
    connected: boolean;
    requested: boolean;
    lastRequest: string;
    practiceId: string;
    integrationType: string;
    apiUrl?: string;
    account_integration_id?: string;
};

const baseIntegrations: Integration[] = [
    {
        id: 1,
        name: "Cornerstone",
        provider: IntegrationThirdParty.Cornerstone,
        imageUrl: Cornerstone,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "2023-11-08 16:42:10.399+00",
        practiceId: "D01B0E90-A02A-4C36-8B3B-5B83BF1506E2",
        integrationType: "pims",
    },
    {
        id: 2,
        name: "AVImark",
        provider: IntegrationThirdParty.AVImark,
        imageUrl: Avimark,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "",
        practiceId: "",
        integrationType: "pims",
    },
    {
        id: 3,
        name: "ImproMed",
        provider: IntegrationThirdParty.ImproMed,
        imageUrl: Impromed,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "",
        practiceId: "",
        integrationType: "pims",
    },
    {
        id: 4,
        name: "VetSpire",
        provider: IntegrationProvider.VetSpire,
        imageUrl: Vetspire,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "",
        practiceId: "",
        integrationType: "pims",
    },
    {
        id: 5,
        name: "EzyVet",
        provider: IntegrationProvider.EzyVet,
        imageUrl: EzyVet,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "",
        practiceId: "",
        integrationType: "pims",
    },
    {
        id: 6,
        name: "Shepherd",
        provider: IntegrationProvider.Shepherd,
        imageUrl: ShepherdLogo,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "",
        practiceId: "",
        integrationType: "pims",
    },
    {
        id: 8,
        name: "DaySmart",
        provider: IntegrationProvider.DaySmart,
        imageUrl: DaySmartLogo,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "",
        practiceId: "",
        integrationType: "pims",
    },
    {
        id: 10,
        name: "Covetrus Pulse",
        provider: IntegrationProvider.Pulse,
        imageUrl: PulseLogo,
        disabled: false,
        connected: false,
        requested: false,
        lastRequest: "",
        practiceId: "",
        integrationType: "pims",
    },
];

/** Whether a name is one of the PIMS cards above. Read at call time (Card.tsx and this file import each other). */
export const isKnownIntegrationName = (name: string) =>
    baseIntegrations.some((integration) => integration.name === name);

/**
 * Integrations page component
 */
const IntegrationsPage = () => {
    const { pmsIntegrationData, containsFeatureFlag, accountData } = useAccountContext();
    const [integrations, setIntegrations] = useState<Integration[]>(baseIntegrations);

    // Same cache entry as BridgeStatus (which renders inside the block below) and as
    // useBridgeUpgradeOffer (which the BridgeUpgradeCta above this whole page reads — it sits in
    // the Settings shell, not here): one key, one `queryFn`, one request. `fetchBridgeStatus` rather than an inline apiFetch deliberately — React Query
    // hands the query function of whichever observer mounted last to all of them, and
    // `fetchBridgeStatus` is the one that THROWS on a failed check instead of resolving to
    // something that reads like `installed: false`.
    //
    // `enabled` is deliberately just "do we have an account id", not a cohort clause, and a
    // reviewer flagged the traffic: before EN-1063 this request only fired inside the beta-gated
    // block below, so this widens it from ~27 accounts to anyone opening this tab. Measured
    // before deciding (production, 2026-09-18) rather than argued:
    //
    //   - Narrowing on the cohort flag would be safe TODAY — all 26 accounts with a live bridge
    //     also have `happy_bridge_beta` — but it reintroduces the bug the gate comment below
    //     exists to describe: `bridge_upgrade_offer`'s natural admin action after a successful
    //     upgrade is to turn it OFF, and `bridgeInstalled` is what keeps the bridge controls on
    //     screen afterwards. Visibility for a working install would once again ride on a
    //     marketing flag.
    //   - Narrowing on the PIMS predicate buys much less than it looks: 779 of 2174 accounts have
    //     a null `current_pims`, which counts as bridge-capable, so the saving is 34% of the
    //     visits to one settings tab.
    //
    // A third of the requests to a rarely-opened tab is not worth a new way to hide bridge
    // controls from a clinic whose bridge is live. Revisit if `GET bridge/status` ever stops
    // being a single indexed lookup on `bridge.bridge_installations`.
    const { data: bridgeStatus } = useQuery({
        queryKey: bridgeStatusQueryKey(accountData?.id),
        queryFn: fetchBridgeStatus,
        staleTime: 60_000,
        enabled: Boolean(accountData?.id),
    });
    const bridgeInstalled = bridgeStatus?.installed === true;
    // Explained at the bridge block in the render below (EN-1063).
    const showBridge = VITE_PUBLIC_ENV !== "production" || accountData?.happy_bridge_beta === true || bridgeInstalled;
    const [connectedIntegrations, setConnectedIntegrations] = useState<Integration[]>([]);
    const [unconnectedIntegrations, setUnconnectedIntegrations] = useState<Integration[]>([]);

    /**
     * Add the import appointments integration if the feature flag is enabled and the integration does not exist
     */
    useEffect(() => {
        if (containsFeatureFlag(FeatureFlags.ENABLE_IMPORT_APPOINTMENTS)) {
            if (!integrations.find((integration) => integration.provider === IntegrationProvider.File)) {
                setIntegrations([
                    ...integrations,
                    {
                        id: 9,
                        name: "Import Appointments",
                        provider: IntegrationProvider.File,
                        imageUrl: ArrowSquareUpIcon,
                        disabled: false,
                        connected: false,
                        requested: false,
                        lastRequest: "",
                        practiceId: "",
                        integrationType: "csv",
                    },
                ]);
            }
        }
    }, [accountData]);

    // This function will be used to determine the connected status and practiceId
    useEffect(() => {
        if (pmsIntegrationData && pmsIntegrationData?.length > 0) {
            const connected: Integration[] = [];
            const updated = integrations?.map((integration) => {
                // Check if this integration is the one that's connected.
                const pmsIntegrationDataItem = pmsIntegrationData.find(
                    (item) =>
                        item.connected_third_party === integration.provider ||
                        item.integrations?.integration_name === integration.provider,
                );

                const isConnected = !!pmsIntegrationDataItem;
                const updatedIntegration = {
                    ...integration,
                    // Update the connected and practiceId properties if this integration is connected.
                    connected: isConnected || integration.connected || false,
                    practiceId: isConnected
                        ? pmsIntegrationDataItem?.practice_id || ""
                        : integration.practiceId || "" || "",
                    lastRequest: isConnected ? pmsIntegrationDataItem?.last_request || "" : "",
                    apiUrl: isConnected ? pmsIntegrationDataItem?.api_url || "" : "",
                    account_integration_id: isConnected ? pmsIntegrationDataItem?.id || "" : "",
                };

                if (isConnected) {
                    connected.push(updatedIntegration);
                }

                return updatedIntegration;
            });

            setConnectedIntegrations(connected);
            setUnconnectedIntegrations(updated.filter((integration) => !integration.connected));
        } else {
            // If pmsIntegrationData is null, set the original integrations array.
            setUnconnectedIntegrations(integrations);
        }
    }, [pmsIntegrationData, integrations]); // Dependencies array for useEffect.
    return (
        <div className="flex-grow flex flex-col gap-5 bg-white border border-gray-200 rounded-md p-8">
            <div className="flex flex-row items-center justify-between w-full">
                <div className="flex flex-col justify-between gap-1">
                    <h2 className="text-lg text-gray-900 font-semibold font-jakarta">Integrations</h2>
                    <p className="text-sm text-gray-600 ">
                        Supercharge your workflow and connect the tool(s) you use every day.
                    </p>
                </div>
            </div>

            {/* HappyDoc Bridge is the primary integration, so whenever it shows it leads the page (EN-1026).

                Two separate questions, and they must not be collapsed back into one. EN-1063.

                WHETHER the bridge block appears at all:
                1. Outside production this is a dev/QA affordance and always available.
                2. `happy_bridge_beta` is the beta entitlement: the hand-held accounts who are
                   meant to reach the raw PIMS picker.
                3. `bridgeInstalled` keeps BridgeStatus and BridgeTroubleshooting on screen for a
                   clinic whose bridge is live, no matter what any marketing flag says. Without
                   it, post-upgrade visibility rode on `bridge_upgrade_offer` — a flag whose
                   natural admin action after a successful upgrade is to turn it OFF, which would
                   silently strip every in-product bridge control from a working install.

                WHICH HALF they get (`showInstaller`): only 1 and 2. The Download button fires
                `POST bridge/setup-integration`, see `BridgeSetupFlow`'s `runSetupAndDownload`,
                with no confirmation and no warning. That call creates the happybridge
                integration; it only goes live immediately when the account has no other
                integration, otherwise after the bridge passes its checks (EN-1302). That is
                acceptable for 27 hand-held beta accounts and not acceptable for the rollout
                cohort, whose route to the same setup is the wizard, which walks the clinic through
                the install and shows when the switch has happened.

                Clause 3 alone previously brought the installer with it, because it mounted the
                whole component. `installed: true` with a `setup_step` short of `done` is exactly
                the resumable state the upgrade offer STAYS visible for (see
                `useBridgeUpgradeOffer`), so a cohort clinic mid-migration saw the announcement
                card and the unguarded Download on the same page — the gap Cursor flagged on
                PR #2156. Splitting the two questions closes it without taking status and
                troubleshooting away from a live install.

                Deliberately still NOT gated on `bridge_upgrade_offer`, which is what it was
                between 5b6301920 and the review that reverted it. */}
            {showBridge && (
                <>
                    <Divider />
                    <BridgeDownload
                        showInstaller={VITE_PUBLIC_ENV !== "production" || accountData?.happy_bridge_beta === true}
                    />
                </>
            )}

            {(connectedIntegrations?.length > 0 || showBridge) && <Divider />}

            <div className="flex flex-col gap-5">
                {connectedIntegrations &&
                    connectedIntegrations?.length > 0 &&
                    connectedIntegrations.map(
                        (integration) =>
                            integration && <ConnectedIntegration key={integration.id} integration={integration} />,
                    )}
            </div>
            <div className="">
                <h3 className="text-sm font-semibold text-gray-900 mb-5 ">
                    {"Practice Information Management Softwares"}
                </h3>
                <div className="grid grid-cols-1 gap-x-5 gap-y-5 lg:grid-cols-2 ">
                    {unconnectedIntegrations &&
                        unconnectedIntegrations?.map((integration: any, index) => (
                            <Cards key={index} integration={integration} />
                        ))}
                </div>
            </div>
        </div>
    );
};

export default IntegrationsPage;
